import React from 'react';
import { IconScissors, IconRotate } from '@tabler/icons-react';
import { useMemoryGameStore } from '../../../stores/index.js';
import MemoryCard, { PlaceholderCard } from './MemoryCard';
import SavedTicketBanner from './SavedTicketBanner';
import { resolveThemeColor } from './colorUtils';
import styles from './MemoryBoard.module.scss';

const MemoryBoard = () => {
    const { cards, matchedPairsCount, totalPairsCount, attempts, hasTicket, announcement, config } =
        useMemoryGameStore();

    const isEightCardLayout = cards.length === 8;
    const isSixCardLayout = cards.length === 6;
    const useThreeColumns = isEightCardLayout || isSixCardLayout;

    const cardColorType = config?.cards?.colorType;
    const cardCustomColor = config?.cards?.customColor;

    const resolvedThemeColor = resolveThemeColor(cardColorType, cardCustomColor);

    return (
        <section className={`${styles.boardSection} my-3`} aria-label="Spielfeld">
            {/* Screen Reader Live Region for WCAG 2.1 AA Compliance */}
            <div className="visually-hidden" aria-live="polite" aria-atomic="true">
                {announcement}
            </div>

            {/* Cards Grid Container */}
            <div className={useThreeColumns ? styles.cardsGrid3Cols : styles.cardsGrid}>
                {isEightCardLayout ? (
                    <>
                        {cards.slice(0, 4).map((card, idx) => (
                            <MemoryCard
                                key={card.id}
                                card={card}
                                index={idx}
                                shouldPulse={idx === 0 && attempts === 0 && !cards.some((c) => c.isFlipped)}
                            />
                        ))}
                        <PlaceholderCard key="placeholder" />
                        {cards.slice(4).map((card, idx) => (
                            <MemoryCard key={card.id} card={card} index={idx + 4} shouldPulse={false} />
                        ))}
                    </>
                ) : (
                    cards.map((card, idx) => (
                        <MemoryCard
                            key={card.id}
                            card={card}
                            index={idx}
                            shouldPulse={idx === 0 && attempts === 0 && !cards.some((c) => c.isFlipped)}
                        />
                    ))
                )}
            </div>

            {/* Stats Block */}
            <div className={`${styles.statsBlock} d-flex flex-column py-2 gap-1 mb-2 w-100 text-dark`}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <IconScissors size={20} stroke={resolvedThemeColor} strokeWidth={2} />
                        <span className="small">Gefundene Paare</span>
                    </div>
                    <span className="small fw-bold">
                        {matchedPairsCount}/{totalPairsCount}
                    </span>
                </div>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                        <IconRotate size={20} stroke={resolvedThemeColor} strokeWidth={2} />
                        <span className="small">Versuche</span>
                    </div>
                    <span className="small fw-bold">{String(attempts).padStart(3, '0')}</span>
                </div>
            </div>

            {/* Saved Ticket Banner (if player saved golden ticket) */}
            {hasTicket && (
                <div className={styles.bannerWrapper}>
                    <SavedTicketBanner />
                </div>
            )}
        </section>
    );
};

export default MemoryBoard;
